import React, { useRef, useState } from 'react';

import { action } from '../../lib/actionHelpers.js';
import getMessageIcon from '../../lib/getMessageIcon.js';
import selectors from '../../selectors/selectors.js';
import sortByProp from '../../lib/sortByProp.js';
import { useHandleClickOutside } from '../../hooks';
import { useSelector } from 'react-redux';

const filterTypeSelectorsMap = {
  userId: 'serviceProviderUsersRaw',
  locationId: 'serviceProviderLocations',
  todoTypeId: 'todoTypes',
  todoStatusId: 'todoStatuses',
  servicePersonId: 'serviceProviderServicePersons',
  serviceId: 'serviceProviderServices',
};

const requiredFilters = ['reportsDateAggregate', 'reportsDateType'];

const filterSetOptionsMap = {
  userId: (n) => n.map((n) => ({ label: `${n.firstName || ''} ${n.lastName || ''}`.trim(), value: n.id })),
  locationId: (n) => n.map((n) => ({ label: `${n.service_location_name || ''}`.trim(), value: n.id })),
  todoTypeId: (n) => n.map((n) => ({ label: `${n.type || ''}`.trim(), value: n.id })),
  todoStatusId: (n) => n.map((n) => ({ label: `${n.status || ''}`.trim(), value: n.id })),
  servicePersonId: (n) => n.map((n) => ({ label: `${n.firstName || ''} ${n.lastName || ''}`.trim(), value: n.id })),
  serviceId: (n) => n.map((n) => ({ label: n.label ? `${n.service_name || ''} - ${n.label || ''}`.trim() : `${n.service_name || ''}`.trim(), value: n.id })).sort(sortByProp('label')),
};

const DefaultFilterItem = ({ item }) => {
  const type = item.type;

  const filterValue = useSelector((state) => state.ui.filters[item.id || item.type]);
  const filterSet = useSelector((state) => (!item.options ? selectors[filterTypeSelectorsMap[type]](state) : null));

  const [search, setSearch] = useState('');
  const [optionsIndex, setOptionsIndex] = useState(0);
  const [showOptions, setShowOptions] = useState(false);
  const searchInput = useRef(null);

  let rawOptions = [...(item.options || [])];
  if (!item.options) {
    if (filterTypeSelectorsMap[type] === undefined) {
      throw new Error(`FilterItem rendered with invalid item type "${item.type}". Must be one of the following: ${Object.keys(filterTypeSelectorsMap).join(', ')}`);
    }
    rawOptions = filterSetOptionsMap[type](filterSet);
  }

  let options = [...rawOptions];
  if (search) {
    options = options.filter((n) => String(n.label).toLowerCase().includes(String(search).toLowerCase()));
    options = options.length === 0 ? [{ label: 'No Results', value: null }] : options;
  }

  let selectedOption = filterValue ? options.find((option) => option.value === filterValue) || {} : null;

  const dropdownRef = useRef(null);
  useHandleClickOutside(dropdownRef, () => {
    setShowOptions(false);
  });

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setOptionsIndex(0);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      setSearch('');
      setShowOptions(false);
    }
  };

  const handleSearchKeyUp = (e) => {
    if (e.key === 'Enter') {
      setSearch('');
      handleClick(item, options[optionsIndex]);
      return;
    }
    if (e.key === 'Escape') {
      setSearch('');
      setShowOptions(false);
      return;
    }
    let newIndex = (
      {
        ArrowUp: (n) => n - 1,
        ArrowDown: (n) => n + 1,
        PageUp: (n) => n - 7,
        PageDown: (n) => n + 7,
        Home: (n) => 0,
        End: (n) => options.length - 1,
      }[e.key] || ((n) => n)
    )(optionsIndex);
    newIndex = newIndex < 0 ? 0 : newIndex;
    newIndex = newIndex > options.length - 1 ? options.length - 1 : newIndex;
    if (newIndex !== optionsIndex) {
      setOptionsIndex(newIndex);
    }
  };

  const setOptionsVisible = () => {
    setShowOptions(true);
    setTimeout(() => {
      if (searchInput.current) {
        searchInput.current.focus();
      }
    }, 100);
  };

  const handleClick = (item, opt) => {
    let option = opt === null && rawOptions.length === 1 && !filterValue ? rawOptions[0] : opt;
    if ((option || {}).value === null) {
      return;
    }
    if (opt === null && rawOptions.length !== 1 && !filterValue) {
      setOptionsVisible();
    }
    if (opt !== null || showOptions === true || options.length === 1) {
      setShowOptions(false);
    }
    setSearch('');
    // Don't allow required filters to be set as undefined. Only show options.
    if ((option || {}).value === undefined && requiredFilters.includes(item.type)) {
      return setOptionsVisible();
    }
    // Due to handleClick being used on top level filter and filter options items we want to prevent redux actions if no option is selected.

    if (rawOptions.length === 1 || filterValue) {
      action(item.customDispatchName ? item.customDispatchName : 'uiPatchFilters', {
        [item.id || item.type]: (option || {}).value || null,
      });
      if (item.onChange) {
        item.onChange((option || {}).value || null);
      }
    }
  };

  const handleDropdownItemClick = (item, opt) => {
    let option = opt === null && rawOptions.length === 1 && !filterValue ? rawOptions[0] : opt;
    if (opt !== null || showOptions === true) {
      setShowOptions(false);
    }
    setSearch('');
    action(item.customDispatchName ? item.customDispatchName : 'uiPatchFilters', {
      [item.id || item.type]: (option || {}).value || null,
    });
    if (item.onChange) {
      item.onChange((option || {}).value || null);
    }
  };

  return (
    <div ref={dropdownRef} className={`filter-item ${selectedOption ? 'selected' : ''}`}>
      <button
        className={`filter-item-btn btn`}
        data-title={selectedOption ? item.label : undefined}
        onClick={(e) => {
          e.stopPropagation();
          handleClick(item, selectedOption ? 'reset' : null);
        }}>
        {selectedOption && selectedOption.label ? (
          <>
            <div className={`filter-item-label`}>{selectedOption.label}</div>
            {item.count !== undefined && <div className={`count ${item.countClass || ''}`}>{item.count}</div>}
          </>
        ) : (
          <>
            <div className={`filter-item-label`}>{filterValue || item.label}</div>
            {item.count !== undefined && <div className={`count ${item.countClass || ''}`}>{item.count}</div>}
          </>
        )}
        {selectedOption && !requiredFilters.includes(item.type) ? (
          <>
            <img alt="Close" className={`filter-item-close-btn`} src={getMessageIcon('icon-close')} />
          </>
        ) : null}
      </button>
      <div className={`filter-item-options ${showOptions ? 'active' : ''}`}>
        <input type="text" className={`options-search-input`} placeholder={`Search`} onChange={handleSearchChange} value={search} ref={searchInput} onKeyDown={handleSearchKeyDown} onKeyUp={handleSearchKeyUp} />
        <div className={`options-list`}>
          {options.map((option, j) => (
            <button className={`btn ${j === optionsIndex ? 'active' : ''} ${option.value === null ? 'placeholder' : ''}`} key={j} onClick={(e) => handleDropdownItemClick(item, option)}>
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DefaultFilterItem;
